import { Box, Divider, Skeleton, Typography } from "@mui/material";
import { Fragment, type ReactNode } from "react";

import {
  CALIBRATE_COLORS,
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_RADIUS_MD,
  NAV_SECTION_COLORS,
} from "components/hoc/Theme";

// The Secure Exchange tile on the home page sits in the Workspace section.
const ICON_COLORS = NAV_SECTION_COLORS.workspace;

export interface HeaderCounter {
  label: string;
  value: number | null | undefined;
}

interface SecureExchangeHeaderProps {
  icon: ReactNode;
  title: ReactNode;
  /** Beside the title, such as a project's status. */
  badge?: ReactNode;
  subtitle?: ReactNode;
  counters?: HeaderCounter[];
  /** On the right instead of (or before) the counters. */
  actions?: ReactNode;
  loading?: boolean;
}

/** The card on top of every Secure Exchange page: an icon box in the colours of
 * the Home tile, the title and, on the right, the page's counters or actions. */
export default function SecureExchangeHeader({
  icon,
  title,
  badge,
  subtitle,
  counters = [],
  actions,
  loading = false,
}: SecureExchangeHeaderProps) {
  return (
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: `${CALIBRATE_RADIUS_MD}px`,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1,
          bgcolor: ICON_COLORS.iconBackground,
          color: ICON_COLORS.icon,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Typography
            component="h1"
            noWrap
            sx={{
              fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
              fontWeight: 700,
              fontSize: { xs: 18, sm: 22 },
              lineHeight: 1.25,
              color: CALIBRATE_COLORS.primaryBrand,
              minWidth: 0,
            }}
          >
            {loading ? <Skeleton width={220} /> : title}
          </Typography>
          {!loading && badge}
        </Box>
        {subtitle ? (
          <Typography
            noWrap
            sx={{
              fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
              fontSize: 13,
              color: CALIBRATE_COLORS.secondaryText,
              mt: 0.25,
            }}
          >
            {loading ? <Skeleton width={160} /> : subtitle}
          </Typography>
        ) : null}
      </Box>
      {actions}
      {counters.length > 0 ? (
        <Box
          sx={{ display: "flex", alignItems: "stretch", gap: 2, flexShrink: 0 }}
        >
          {counters.map((counter, index) => (
            <Fragment key={counter.label}>
              {index > 0 ? <Divider orientation="vertical" flexItem /> : null}
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    lineHeight: 1.3,
                    color: CALIBRATE_COLORS.primaryBrand,
                  }}
                >
                  {counter.value ?? (loading ? <Skeleton width={24} /> : "-")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: CALIBRATE_COLORS.secondaryText,
                  }}
                >
                  {counter.label}
                </Typography>
              </Box>
            </Fragment>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
